import { useState, useEffect, useCallback, useRef } from 'react'
import { api } from '../context/AuthContext'

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt    = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'
const fmtTs  = (d) => d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'
const money  = (n) => n != null ? `${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} RWF` : '—'
const moneyN = (n) => Number(n || 0).toFixed(2)

const avatarBg = (str = '') => {
  const c = ['#fde8d8','#dff5e8','#e8f4ff','#fef3d0','#ede8ff','#fce7f3']
  let h = 0; for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h)
  return c[Math.abs(h) % c.length]
}
const initials = (n = '') => (n.trim().charAt(0) || '?').toUpperCase()

// ── Tier metadata ─────────────────────────────────────────────────────────────
const TIER_META = {
  bronze:   { label: 'Bronze',   icon: '🥉', color: '#92400e', bg: '#fef3c7', border: '#fde68a' },
  silver:   { label: 'Silver',   icon: '🥈', color: '#374151', bg: '#f3f4f6', border: '#d1d5db' },
  gold:     { label: 'Gold',     icon: '🥇', color: '#b45309', bg: '#fffbeb', border: '#fcd34d' },
  platinum: { label: 'Platinum', icon: '💎', color: '#1d4ed8', bg: '#eff6ff', border: '#bfdbfe' },
}

// ── PDF Export ────────────────────────────────────────────────────────────────
function exportSellerPDF(shops, period, commPct = 0, ipayPct = 0, filters = {}) {
  const VERIF_LABEL = {
    verified:   'Verified',
    pending:    'Pending Review',
    rejected:   'REJECTED — Payout Blocked',
    unverified: 'UNVERIFIED — Payout Blocked',
    blocked:    'Blocked (Unverified + Rejected)',
  }
  const VERIF_COLOR = {
    verified: '#15803d', pending: '#1d4ed8',
    rejected: '#b91c1c', unverified: '#92400e', blocked: '#b91c1c',
  }
  const VERIF_BG = {
    verified: '#d1fae5', pending: '#dbeafe',
    rejected: '#fee2e2', unverified: '#fef3c7', blocked: '#fee2e2',
  }
  const VERIF_BORDER = {
    verified: '#6ee7b7', pending: '#93c5fd',
    rejected: '#fca5a5', unverified: '#fcd34d', blocked: '#fca5a5',
  }

  const filterNote = []
  if (filters.verification) filterNote.push(`Verification: ${VERIF_LABEL[filters.verification] || filters.verification}`)
  if (filters.category)     filterNote.push(`Category: ${filters.category}`)
  if (filters.search)       filterNote.push(`Search: "${filters.search}"`)

  const sumProduct  = shops.reduce((a, s) => a + Number(s.product_payout        || 0), 0)
  const sumDeliv    = shops.reduce((a, s) => a + Number(s.self_delivery_payout   || 0), 0)
  const sumTips     = shops.reduce((a, s) => a + Number(s.tips_total             || 0), 0)
  const sumTotal    = shops.reduce((a, s) => a + Number(s.total_payout           || 0), 0)
  const sumOrders   = shops.reduce((a, s) => a + Number(s.order_count            || 0), 0)
  const blockedCount = shops.filter(s => s.payout_blocked).length

  const rows = shops.map(s => {
    const vs      = s.verification_status ?? 'unverified'
    const blocked = !!s.payout_blocked
    const rowBg   = blocked ? '#fff5f5' : (vs === 'verified' ? '#f0fdf4' : '#fff')
    return `<tr style="background:${rowBg}">
      <td><strong>${s.shop_name || '—'}</strong><br><span style="color:#9a9a94;font-size:9px">${s.owner_phone || ''}</span></td>
      <td>${s.category || '—'}</td>
      <td>${s.owner_name || '—'}</td>
      <td style="text-align:center">${s.order_count}</td>
      <td style="text-align:right;color:#15803d;font-weight:700">${moneyN(s.product_payout)}</td>
      <td style="text-align:right;color:#1d4ed8">${Number(s.self_delivery_payout) > 0 ? moneyN(s.self_delivery_payout) : '—'}</td>
      <td style="text-align:right;color:#7c3aed">${Number(s.tips_total) > 0 ? moneyN(s.tips_total) : '—'}</td>
      <td style="text-align:right;font-weight:900">${moneyN(s.total_payout)}</td>
      <td style="color:${VERIF_COLOR[vs] || '#374151'};font-size:9px;font-weight:700">${blocked ? '⛔ ' : (vs === 'verified' ? '✓ ' : '')}${VERIF_LABEL[vs] || vs}</td>
    </tr>`
  }).join('')

  const vf = filters.verification
  const badgeHtml = vf
    ? `<span style="display:inline-block;padding:3px 10px;border-radius:4px;font-size:10px;font-weight:700;background:${VERIF_BG[vf]||'#f3f4f6'};color:${VERIF_COLOR[vf]||'#374151'};border:1px solid ${VERIF_BORDER[vf]||'#e5e7eb'};margin-bottom:10px">${VERIF_LABEL[vf] || vf}</span>`
    : ''

  const html = `<!DOCTYPE html><html><head><title>Kudos Seller Payouts — ${period}</title>
  <style>
    *{margin:0;padding:0;box-sizing:border-box}
    body{font-family:Arial,sans-serif;font-size:11px;color:#111;padding:20px}
    h1{font-size:17px;font-weight:900;margin-bottom:4px}
    .meta{font-size:10px;color:#666;margin-bottom:8px;font-family:monospace}
    .warn{background:#fef2f2;border:1px solid #fecaca;border-radius:6px;padding:7px 12px;font-size:10px;color:#b91c1c;margin-bottom:10px;font-weight:700}
    table{width:100%;border-collapse:collapse;font-size:10px}
    th{background:#f3f4f6;text-align:left;padding:6px 8px;font-size:8px;text-transform:uppercase;letter-spacing:.07em;color:#6b7280;border-bottom:2px solid #e5e7eb}
    td{padding:6px 8px;border-bottom:1px solid #f3f4f6;vertical-align:top}
    .tfooter td{font-weight:700;background:#f9f9f7;border-top:2px solid #e5e7eb}
    @media print{body{padding:10px}@page{margin:15mm}}
  </style></head><body>
  <h1>Kudos Seller Payouts</h1>
  <div class="meta">Period: ${period} · ${shops.length} shops · ${sumOrders} orders${filterNote.length ? ' · Filtered: ' + filterNote.join(' · ') : ''}</div>
  ${badgeHtml}
  ${blockedCount > 0 ? `<div class="warn">⛔ ${blockedCount} shop${blockedCount > 1 ? 's' : ''} have payout BLOCKED — do not process these payouts until verified.</div>` : ''}
  <table>
    <thead><tr>
      <th>Shop</th><th>Category</th><th>Owner</th><th>Orders</th>
      <th style="text-align:right">Product Payout (RWF)</th>
      <th style="text-align:right">Self-Delivery (RWF)</th>
      <th style="text-align:right">Tips Net (RWF)</th>
      <th style="text-align:right">Total Payout (RWF)</th>
      <th>Verification</th>
    </tr></thead>
    <tbody>${rows}
    <tr class="tfooter">
      <td colspan="3"><strong>TOTAL (${shops.length} shops)</strong></td>
      <td style="text-align:center">${sumOrders}</td>
      <td style="text-align:right;color:#15803d">${moneyN(sumProduct)}</td>
      <td style="text-align:right;color:#1d4ed8">${moneyN(sumDeliv)}</td>
      <td style="text-align:right;color:#7c3aed">${moneyN(sumTips)}</td>
      <td style="text-align:right">${moneyN(sumTotal)}</td>
      <td></td>
    </tr></tbody>
  </table>
  <div style="margin-top:14px;font-size:9px;color:#9a9a94;font-family:monospace">
    Generated ${new Date().toLocaleString('en-GB')} · RWF amounts · iPay ${(ipayPct*100).toFixed(0)}% applied${commPct > 0 ? ` · Delivery commission ${commPct}%` : ''}
  </div>
  <script>window.onload=function(){window.print()}<\/script>
  </body></html>`

  const w = window.open('', '_blank')
  if (!w) return
  w.document.write(html)
  w.document.close()
}

// ── CSV Export ────────────────────────────────────────────────────────────────
function exportSellerCSV(shops, period, commPct = 0, ipayPct = 0) {
  const escape   = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`
  const hasComm  = commPct > 0
  const iPctLabel = ipayPct > 0 ? ` (${(ipayPct * 100).toFixed(0)}%)` : ''
  const VERIF_LABEL = { verified: 'Verified', pending: 'Pending Review', rejected: 'REJECTED — Payout Blocked', unverified: 'UNVERIFIED — Payout Blocked' }
  const headers = [
    'Shop Name', 'Category', 'Owner', 'Phone', 'Orders', 'Tier',
    'Verification Status', 'Payout Blocked',
    'Platform Fee % (Base)', 'Platform Fee % (Effective)', 'Tier Discount (RWF)',
    'Gross Subtotal (RWF)', 'iPay Fee (RWF)', 'Platform Fee (RWF)', 'Product Payout (RWF)',
    'Self-Delivery Gross (RWF)',
    ...(hasComm ? [`Delivery Commission (${commPct}%) (RWF)`] : []),
    'Self-Delivery Net (RWF)',
    `Tips Gross (RWF)`, `Tips iPay Fee${iPctLabel} (RWF)`, 'Tips Platform Fee (RWF)', 'Tips Net Payout (RWF)', 'Tip Count',
    'Total Payout (RWF)',
  ]
  const lines = shops.map(s => {
    const basePct       = Number(s.platform_pct_base || 0)
    const effPct        = Number(s.platform_pct_effective || basePct)
    const disc          = Number(s.tier_discount || 0)
    const saving        = +(Number(s.gross_subtotal || 0) * disc).toFixed(2)
    const tipsGross      = Number(s.tips_gross_total    ?? s.tips_total ?? 0)
    const tipsPlatform   = Number(s.tips_platform_total ?? 0)
    const tipsNet        = Number(s.tips_total           ?? 0)
    const tipsIpay        = Number(s.tips_ipay_total ?? +(tipsGross - tipsPlatform - tipsNet).toFixed(2))
    const vs         = s.verification_status ?? 'unverified'
    return [
      s.shop_name, s.category || '', s.owner_name || '', s.owner_phone || '', s.order_count,
      s.tier || 'bronze',
      VERIF_LABEL[vs] || vs, s.payout_blocked ? 'YES' : 'No',
      (basePct * 100).toFixed(0) + '%', (effPct * 100).toFixed(0) + '%', moneyN(saving),
      moneyN(s.gross_subtotal), moneyN(s.ipay_total), moneyN(s.platform_total), moneyN(s.product_payout),
      moneyN(s.self_delivery_gross),
      ...(hasComm ? [moneyN(s.self_delivery_commission)] : []),
      moneyN(s.self_delivery_payout),
      moneyN(tipsGross), moneyN(tipsIpay), moneyN(tipsPlatform), moneyN(tipsNet),
      Number(s.tips_count || 0),
      moneyN(s.total_payout),
    ].map(escape).join(',')
  })
  const csv  = [headers.join(','), ...lines].join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url  = URL.createObjectURL(blob)
  const a    = document.createElement('a'); a.href = url
  a.download = `kudos-seller-payouts-${period}.csv`; a.click()
  URL.revokeObjectURL(url)
}

// ── Sub-components ────────────────────────────────────────────────────────────
function TierBadge({ tier, small }) {
  const m = TIER_META[tier] || TIER_META.bronze
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3,
      background: m.bg, color: m.color, border: `1px solid ${m.border}`,
      fontSize: small ? 8 : 10, fontWeight: 700, padding: small ? '1px 5px' : '2px 7px',
      borderRadius: 4, fontFamily: "'JetBrains Mono',monospace", whiteSpace: 'nowrap' }}>
      {m.icon} {m.label}
    </span>
  )
}

function SummaryCard({ label, value, sub, accent, bold }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '16px 18px', flex: 1 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: bold ? 22 : 20, fontWeight: 800, letterSpacing: '-0.5px', color: accent || '#111317' }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: '#9a9a94', marginTop: 4, fontFamily: "'JetBrains Mono',monospace" }}>{sub}</div>}
    </div>
  )
}

function PagBtn({ label, onClick, disabled, active }) {
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ height: 32, minWidth: 32, padding: '0 10px', borderRadius: 6, border: `1px solid ${active ? '#111317' : '#e9e9e4'}`, background: active ? '#111317' : '#fff', color: active ? '#fff' : disabled ? '#d1d5db' : '#374151', fontSize: 12, fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer', fontFamily: "'JetBrains Mono',monospace" }}>
      {label}
    </button>
  )
}

// ── Seller Order Drawer ───────────────────────────────────────────────────────
function SellerDrawer({ shop, dateFrom, dateTo, onClose }) {
  const [orders,       setOrders]       = useState([])
  const [tips,         setTips]         = useState([])
  const [totals,       setTotals]       = useState(null)
  const [commPct,      setCommPct]      = useState(0)
  const [ipayPct,      setIpayPct]      = useState(0)
  const [loading,      setLoading]      = useState(true)
  const [tab,          setTab]          = useState('orders')
  const [tierData,     setTierData]     = useState(null)
  const [payoutBlocked, setPayoutBlocked] = useState(false)
  const [verifStatus,  setVerifStatus]  = useState('unverified')

  useEffect(() => {
    if (!shop) return
    setLoading(true)
    setTab('orders')
    api.get(`/payments/admin/seller-payout-report/${shop.shop_id}?date_from=${dateFrom}&date_to=${dateTo}`)
      .then(res => {
        setOrders(res.data.orders   || [])
        setTips(res.data.tips       || [])
        setTotals(res.data.totals   || null)
        setCommPct(res.data.driver_comm_pct || 0)
        setIpayPct(res.data.ipay_pct || 0)
        setPayoutBlocked(res.data.payout_blocked ?? false)
        setVerifStatus(res.data.verification_status ?? 'unverified')
        setTierData({
          tier:                   res.data.tier                   ?? 'bronze',
          platform_pct_base:      res.data.platform_pct_base      ?? 0,
          platform_pct_effective: res.data.platform_pct_effective ?? 0,
          tier_discount:          res.data.tier_discount          ?? 0,
          is_overridden:          res.data.is_overridden          ?? false,
        })
      })
      .catch(() => { setOrders([]); setTips([]); setTotals(null); setTierData(null) })
      .finally(() => setLoading(false))
  }, [shop, dateFrom, dateTo])

  if (!shop) return null

  const bg = avatarBg(shop.shop_name || '')
  const selfDeliveryOrders = orders.filter(o => o.is_self_delivery)

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.18)', zIndex: 200 }} />
      <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 560, background: '#fff', zIndex: 201, display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 40px rgba(0,0,0,0.12)', animation: 'slideIn 0.22s ease' }}>
        <style>{`@keyframes slideIn{from{transform:translateX(40px);opacity:0}to{transform:translateX(0);opacity:1}}`}</style>

        {/* Header */}
        <div style={{ padding: '18px 22px', borderBottom: '1px solid #e9e9e4', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 44, height: 44, borderRadius: 10, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 800, flexShrink: 0 }}>{initials(shop.shop_name)}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 800 }}>{shop.shop_name || '—'}</div>
            <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginTop: 2 }}>
              {shop.owner_name} · {shop.owner_phone} · {shop.category || 'Uncategorised'}
            </div>
          </div>
          <div onClick={onClose} style={{ cursor: 'pointer', fontSize: 22, color: '#9a9a94', lineHeight: 1 }}>×</div>
        </div>

        {/* Payout block banner */}
        {payoutBlocked && (
          <div style={{ padding: '12px 22px', borderBottom: '1px solid #fecaca', background: '#fef2f2', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 18 }}>⛔</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: '#b91c1c' }}>
                {verifStatus === 'rejected' ? 'Payout blocked — verification rejected' : 'Payout blocked — not verified'}
              </div>
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                {verifStatus === 'rejected'
                  ? 'A required document was rejected. Seller must re-upload before this payout can be processed.'
                  : 'Seller has not submitted verification documents. Payout should not be processed until verified.'}
              </div>
            </div>
            <span style={{ fontSize: 9, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase', color: '#b91c1c', background: '#fef2f2', border: '1px solid #fecaca', padding: '2px 7px', borderRadius: 4, whiteSpace: 'nowrap' }}>
              {verifStatus}
            </span>
          </div>
        )}

        {/* Payout summary strip */}
        {totals && (
          <div style={{ padding: '14px 22px', borderBottom: '1px solid #e9e9e4', background: '#f0fdf4' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#15803d', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 10 }}>
              Payout Summary · {fmt(dateFrom)} – {fmt(dateTo)} · {orders.length} orders{tips.length > 0 ? ` · ${tips.length} tips` : ''}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
              {[
                { lbl: 'Product Payout',    val: money(totals.product_payout),                              color: '#15803d' },
                { lbl: 'Self-Delivery Net', val: selfDeliveryOrders.length > 0 ? money(totals.delivery_payout) : '—', color: '#1d4ed8' },
                { lbl: `Tips Net (${tips.length})`,     val: totals.tips_total > 0 ? money(totals.tips_total) : '—', color: '#7c3aed' },
                { lbl: 'Total Payout',      val: money(totals.total_payout),                                color: '#111317', bold: true },
              ].map(({ lbl, val, color, bold }) => (
                <div key={lbl} style={{ background: 'rgba(255,255,255,0.7)', borderRadius: 7, padding: '8px 10px', border: '1px solid rgba(0,0,0,0.05)' }}>
                  <div style={{ fontSize: 8, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 3 }}>{lbl}</div>
                  <div style={{ fontSize: bold ? 13 : 12, fontWeight: 800, color, fontFamily: "'JetBrains Mono',monospace" }}>{val}</div>
                </div>
              ))}
            </div>
            {totals.delivery_gross > 0 && commPct > 0 && (
              <div style={{ marginTop: 8, fontSize: 10, color: '#6b7280', fontFamily: "'JetBrains Mono',monospace" }}>
                Self-delivery: {money(totals.delivery_gross)} gross − {commPct}% commission = {money(totals.delivery_payout)} net
              </div>
            )}
            {tierData && (
              <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <TierBadge tier={tierData.tier} />
                {tierData.tier_discount > 0 ? (
                  <span style={{ fontSize: 10, color: '#15803d', fontFamily: "'JetBrains Mono',monospace" }}>
                    Platform fee: {(tierData.platform_pct_effective * 100).toFixed(0)}%
                    <span style={{ color: '#9a9a94' }}> (base {(tierData.platform_pct_base * 100).toFixed(0)}% − {(tierData.tier_discount * 100).toFixed(0)}pp tier discount)</span>
                    {' '}· Saved: {money(totals ? (totals.gross_subtotal || 0) * tierData.tier_discount : 0)}
                  </span>
                ) : (
                  <span style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>
                    Platform fee: {(tierData.platform_pct_base * 100).toFixed(0)}% base rate — upgrade to save
                  </span>
                )}
                {tierData.is_overridden && <span style={{ fontSize: 8, color: '#b45309', fontFamily: "'JetBrains Mono',monospace", background: '#fffbeb', border: '1px solid #fde68a', padding: '1px 5px', borderRadius: 3 }}>ADMIN OVERRIDE</span>}
              </div>
            )}
          </div>
        )}

        {/* Tab bar */}
        <div style={{ display: 'flex', gap: 0, padding: '8px 22px', borderBottom: '1px solid #e9e9e4', background: '#fafaf8' }}>
          {[
            { id: 'orders', label: `Orders (${orders.length})` },
            { id: 'tips',   label: `Tips (${tips.length})` },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{ padding: '6px 14px', borderRadius: 6, border: 'none', background: tab === t.id ? '#111317' : 'transparent', color: tab === t.id ? '#fff' : '#9a9a94', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", marginRight: 4 }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <div style={{ padding: '48px 0', textAlign: 'center', color: '#9a9a94', fontSize: 13 }}>Loading…</div>
          ) : tab === 'orders' ? (
            orders.length === 0 ? (
              <div style={{ padding: '48px 22px', textAlign: 'center', color: '#9a9a94', fontSize: 13 }}>No paid orders in this period.</div>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '0.5fr 1.4fr 1fr 1fr 1fr', padding: '10px 22px', borderBottom: '1px solid #e9e9e4', background: '#f9f9f7' }}>
                  {['#', 'Buyer', 'Order Total', 'Product Payout', 'Delivery'].map(h => (
                    <div key={h} style={{ fontSize: 9, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: "'JetBrains Mono',monospace" }}>{h}</div>
                  ))}
                </div>

                {orders.map((o, i) => (
                  <div key={o.order_id || i} style={{ display: 'grid', gridTemplateColumns: '0.5fr 1.4fr 1fr 1fr 1fr', padding: '11px 22px', borderBottom: i < orders.length - 1 ? '1px solid #f3f3f0' : 'none', alignItems: 'center' }}>
                    <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>#{o.order_id}</div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.buyer_name || 'Anonymous'}</div>
                      <div style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{fmtTs(o.created_at)}</div>
                    </div>
                    <div style={{ fontSize: 11, fontFamily: "'JetBrains Mono',monospace", fontWeight: 600 }}>{moneyN(o.total_amount)}</div>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace", color: '#15803d' }}>{moneyN(o.product_payout)}</div>
                      {Number(o.ipay_fee) > 0 && (
                        <div style={{ fontSize: 9, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginTop: 1 }}>sub {moneyN(o.shop_subtotal)} −fees</div>
                      )}
                    </div>
                    <div>
                      {o.is_self_delivery ? (
                        <>
                          <div style={{ fontSize: 12, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace", color: '#1d4ed8' }}>{moneyN(o.delivery_payout)}</div>
                          {Number(o.delivery_gross) > 0 && commPct > 0 && (
                            <div style={{ fontSize: 9, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginTop: 1 }}>{moneyN(o.delivery_gross)} −{commPct}%</div>
                          )}
                        </>
                      ) : (
                        <div style={{ fontSize: 10, color: '#d1d5db', fontStyle: 'italic' }}>Driver</div>
                      )}
                    </div>
                  </div>
                ))}

                {totals && (
                  <div style={{ display: 'grid', gridTemplateColumns: '0.5fr 1.4fr 1fr 1fr 1fr', padding: '12px 22px', borderTop: '2px solid #e9e9e4', background: '#f0fdf4', alignItems: 'center' }}>
                    <div /><div style={{ fontSize: 11, fontWeight: 800, color: '#111317' }}>Total ({orders.length})</div>
                    <div />
                    <div style={{ fontSize: 13, fontWeight: 900, fontFamily: "'JetBrains Mono',monospace", color: '#15803d' }}>{moneyN(totals.product_payout)}</div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 900, fontFamily: "'JetBrains Mono',monospace", color: '#1d4ed8' }}>{moneyN(totals.delivery_payout)}</div>
                      {totals.delivery_commission > 0 && (
                        <div style={{ fontSize: 9, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginTop: 1 }}>comm {moneyN(totals.delivery_commission)}</div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )
          ) : (
            /* Tips tab */
            tips.length === 0 ? (
              <div style={{ padding: '48px 22px', textAlign: 'center', color: '#9a9a94', fontSize: 13 }}>No tips received in this period.</div>
            ) : (
              <>
                {ipayPct > 0 && (
                  <div style={{ background: '#fffbeb', borderBottom: '1px solid #fde68a', padding: '8px 22px', fontSize: 11, color: '#92400e', fontFamily: "'JetBrains Mono',monospace" }}>
                    ℹ️ Gross (buyer paid) → iPay fee ({(ipayPct*100).toFixed(0)}%) → platform tip commission deducted → net payout to seller
                  </div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: '0.4fr 1.2fr 0.6fr 0.8fr 0.7fr 0.7fr 0.8fr 0.7fr', padding: '10px 22px', borderBottom: '1px solid #e9e9e4', background: '#f9f9f7' }}>
                  {['', 'Tipper', 'Date', 'Gross Tip', `iPay ${(ipayPct*100).toFixed(0)}%`, 'Platform', 'Net Payout', 'Method'].map(h => (
                    <div key={h} style={{ fontSize: 9, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: "'JetBrains Mono',monospace" }}>{h}</div>
                  ))}
                </div>

                {tips.map((t, i) => {
                  const gross   = Number(t.gross_amount || t.amount || 0)
                  const platFee = Number(t.platform_fee || 0)
                  const ipayFee = Number(t.ipay_fee     || +(gross * ipayPct).toFixed(2))
                  const net     = Number(t.net_amount   || gross - ipayFee - platFee)
                  return (
                    <div key={t.tip_id || i} style={{ display: 'grid', gridTemplateColumns: '0.4fr 1.2fr 0.6fr 0.8fr 0.7fr 0.7fr 0.8fr 0.7fr', padding: '11px 22px', borderBottom: i < tips.length - 1 ? '1px solid #f3f3f0' : 'none', alignItems: 'center' }}>
                      <div style={{ fontSize: 20, lineHeight: 1 }}>{t.emoji || '💜'}</div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.buyer_name || 'Anonymous'}</div>
                        <div style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{t.buyer_phone || '—'}</div>
                      </div>
                      <div style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{fmtTs(t.created_at)}</div>
                      <div style={{ fontSize: 12, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: '#374151' }}>{moneyN(gross)}</div>
                      <div style={{ fontSize: 11, fontFamily: "'JetBrains Mono',monospace", color: '#b91c1c' }}>− {moneyN(ipayFee)}</div>
                      <div style={{ fontSize: 11, fontFamily: "'JetBrains Mono',monospace", color: '#7c3aed' }}>− {moneyN(platFee)}</div>
                      <div style={{ fontSize: 13, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace", color: '#7c3aed' }}>{moneyN(net)}</div>
                      <div>
                        <span style={{ display: 'inline-block', background: '#f3f4f6', color: '#374151', fontSize: 9, fontWeight: 700, padding: '3px 7px', borderRadius: 4, textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: "'JetBrains Mono',monospace" }}>
                          {t.payment_method || '—'}
                        </span>
                      </div>
                    </div>
                  )
                })}

                {/* Tips total footer */}
                <div style={{ display: 'grid', gridTemplateColumns: '0.4fr 1.2fr 0.6fr 0.8fr 0.7fr 0.7fr 0.8fr 0.7fr', padding: '12px 22px', borderTop: '2px solid #e9e9e4', background: '#faf5ff', alignItems: 'center' }}>
                  <div /><div style={{ fontSize: 11, fontWeight: 800, color: '#7c3aed' }}>Total ({tips.length} tips)</div>
                  <div />
                  <div style={{ fontSize: 12, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: '#374151' }}>
                    {moneyN(totals?.tips_gross_total || tips.reduce((s,t)=>s+Number(t.gross_amount||t.amount||0),0))}
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: '#b91c1c' }}>
                    − {moneyN(totals?.tips_ipay_total || tips.reduce((s,t)=>s+Number(t.ipay_fee||+(Number(t.gross_amount||t.amount||0)*ipayPct).toFixed(2)),0))}
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: '#7c3aed' }}>
                    − {moneyN(totals?.tips_platform_total || tips.reduce((s,t)=>s+Number(t.platform_fee||0),0))}
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 900, fontFamily: "'JetBrains Mono',monospace", color: '#7c3aed' }}>
                    {moneyN(totals?.tips_total)}
                  </div>
                  <div />
                </div>
              </>
            )
          )}
        </div>
      </div>
    </>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function SellerPayment() {
  const now        = new Date()
  const monthStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`
  const monthEnd   = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10)

  const [shops,        setShops]        = useState([])
  const [loading,      setLoading]      = useState(true)
  const [error,        setError]        = useState('')
  const [dateFrom,     setDateFrom]     = useState(monthStart)
  const [dateTo,       setDateTo]       = useState(monthEnd)
  const [searchInput,  setSearchInput]  = useState('')
  const [search,       setSearch]       = useState('')
  const [category,     setCategory]     = useState('')
  const [categories,   setCategories]   = useState([])
  const [verification, setVerification] = useState('')
  const [page,         setPage]         = useState(1)
  const [totalPages,   setTotalPages]   = useState(1)
  const [totalCount,   setTotalCount]   = useState(0)
  const [selected,     setSelected]     = useState(null)
  const [exporting,    setExporting]    = useState(false)
  const [exportingPdf, setExportingPdf] = useState(false)
  const [commPct,      setCommPct]      = useState(0)
  const [meta,         setMeta]         = useState({ ipay_pct: 0, platform_pct: 0 })
  const [summary,      setSummary]      = useState(null)
  const fetchIdRef = useRef(0)

  const PER_PAGE = 25

  useEffect(() => {
    api.get('/payments/admin/shop-categories')
      .then(res => setCategories(res.data.categories || []))
      .catch(() => setCategories([]))
  }, [])

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 400)
    return () => clearTimeout(t)
  }, [searchInput])

  const fetchReport = useCallback(async () => {
    const id = ++fetchIdRef.current
    setLoading(true); setError('')
    try {
      const params = new URLSearchParams({
        page, limit: PER_PAGE,
        date_from: dateFrom, date_to: dateTo,
        ...(search       && { search }),
        ...(category     && { category }),
        ...(verification && { verification }),
      })
      const res = await api.get(`/payments/admin/seller-payout-report?${params}`)
      if (id !== fetchIdRef.current) return
      setShops(res.data.shops || [])
      setSummary(res.data.summary || null)
      setTotalPages(res.data.pages || 1)
      setTotalCount(res.data.total  || 0)
      setCommPct(res.data.driver_comm_pct || 0)
      setMeta({ ipay_pct: res.data.ipay_pct || 0, platform_pct: res.data.platform_pct || 0 })
    } catch {
      if (id !== fetchIdRef.current) return
      setError('Failed to load seller payout report.')
    } finally {
      if (id === fetchIdRef.current) setLoading(false)
    }
  }, [page, dateFrom, dateTo, search, category, verification])

  useEffect(() => { fetchReport() },                   [fetchReport])
  useEffect(() => { setPage(1) },                      [dateFrom, dateTo, search, category, verification])

  const handleExportCSV = async () => {
    setExporting(true)
    try {
      const params = new URLSearchParams({ page: 1, limit: 9999, date_from: dateFrom, date_to: dateTo, ...(search && { search }), ...(category && { category }), ...(verification && { verification }) })
      const res = await api.get(`/payments/admin/seller-payout-report?${params}`)
      exportSellerCSV(res.data.shops || shops, `${dateFrom}_${dateTo}`, commPct, meta.ipay_pct)
    } catch {
      exportSellerCSV(shops, `${dateFrom}_${dateTo}`, commPct, meta.ipay_pct)
    } finally {
      setExporting(false)
    }
  }

  const handleExportPDF = async () => {
    setExportingPdf(true)
    try {
      const params = new URLSearchParams({ page: 1, limit: 9999, date_from: dateFrom, date_to: dateTo, ...(search && { search }), ...(category && { category }), ...(verification && { verification }) })
      const res = await api.get(`/payments/admin/seller-payout-report?${params}`)
      exportSellerPDF(res.data.shops || shops, `${dateFrom}_${dateTo}`, commPct, meta.ipay_pct, { verification, category, search })
    } catch {
      exportSellerPDF(shops, `${dateFrom}_${dateTo}`, commPct, meta.ipay_pct, { verification, category, search })
    } finally {
      setExportingPdf(false)
    }
  }

  const periodLabel = `${fmt(dateFrom)} – ${fmt(dateTo)}`

  // Summary totals cover ALL matching shops (not just current page) — from backend
  const sumProduct    = Number(summary?.product_payout           || 0)
  const sumDelivGross = Number(summary?.self_delivery_gross      || 0)
  const sumDelivComm  = Number(summary?.self_delivery_commission || 0)
  const sumDelivNet   = Number(summary?.self_delivery_payout     || 0)
  const sumTipsGross  = Number(summary?.tips_gross_total          || 0)
  const sumTipsIpay   = Number(summary?.tips_ipay_total           || 0)
  const sumTips       = Number(summary?.tips_total               || 0)
  const sumTipsCount  = Number(summary?.tips_count               || 0)
  const sumTotal      = Number(summary?.total_payout             || 0)
  const sumOrders     = Number(summary?.order_count              || 0)

  // Page-level aggregates (for table footer)
  const pageProduct    = shops.reduce((s, r) => s + Number(r.product_payout         || 0), 0)
  const pageDelivGross = shops.reduce((s, r) => s + Number(r.self_delivery_gross     || 0), 0)
  const pageDelivNet   = shops.reduce((s, r) => s + Number(r.self_delivery_payout    || 0), 0)
  const pageTips       = shops.reduce((s, r) => s + Number(r.tips_total              || 0), 0)
  const pageGrandTotal = shops.reduce((s, r) => s + Number(r.total_payout            || 0), 0)

  return (
    <div style={{ padding: '28px 32px 60px', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.4px' }}>Seller Payouts</div>
          <div style={{ fontSize: 13, color: '#9a9a94', marginTop: 4 }}>Product revenue and self-delivery fee payouts per shop.</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {verification && (
            <span style={{ fontSize: 10, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", padding: '3px 8px', borderRadius: 4,
              color: verification === 'verified' ? '#15803d' : verification === 'blocked' || verification === 'rejected' ? '#b91c1c' : '#1d4ed8',
              background: verification === 'verified' ? '#d1fae5' : verification === 'blocked' || verification === 'rejected' ? '#fee2e2' : '#dbeafe',
              border: `1px solid ${verification === 'verified' ? '#6ee7b7' : verification === 'blocked' || verification === 'rejected' ? '#fca5a5' : '#93c5fd'}`,
              whiteSpace: 'nowrap' }}>
              {{'verified':'✓ Verified','pending':'⏳ Pending','unverified':'⚠ Unverified','rejected':'✕ Rejected','blocked':'⛔ Blocked'}[verification]}
            </span>
          )}
          <button
            onClick={handleExportCSV}
            disabled={exporting || shops.length === 0}
            style={{ height: 38, padding: '0 14px', borderRadius: 8, border: 'none', background: exporting ? '#bfdbfe' : '#1d4ed8', color: '#fff', fontSize: 12, fontWeight: 700, cursor: exporting ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", display: 'flex', alignItems: 'center', gap: 6, boxShadow: '0 2px 8px rgba(29,78,216,0.18)' }}>
            {exporting ? '⏳…' : '⬇ CSV'}
          </button>
          <button
            onClick={handleExportPDF}
            disabled={exportingPdf || shops.length === 0}
            style={{ height: 38, padding: '0 14px', borderRadius: 8, border: 'none', background: exportingPdf ? '#e9d5ff' : '#7c3aed', color: '#fff', fontSize: 12, fontWeight: 700, cursor: exportingPdf ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", display: 'flex', alignItems: 'center', gap: 6, boxShadow: '0 2px 8px rgba(124,58,237,0.2)' }}>
            {exportingPdf ? '⏳…' : '⬇ PDF'}
          </button>
        </div>
      </div>

      {/* Filters row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '12px 16px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, color: '#9a9a94', fontWeight: 600 }}>From</span>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            style={{ border: '1px solid #e9e9e4', borderRadius: 7, padding: '7px 10px', fontSize: 12, fontWeight: 600, color: '#111317', outline: 'none', fontFamily: "'JetBrains Mono',monospace" }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, color: '#9a9a94', fontWeight: 600 }}>To</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            style={{ border: '1px solid #e9e9e4', borderRadius: 7, padding: '7px 10px', fontSize: 12, fontWeight: 600, color: '#111317', outline: 'none', fontFamily: "'JetBrains Mono',monospace" }} />
        </div>

        {/* Month shortcuts */}
        {[0, 1, 2].map(monthsAgo => {
          const d    = new Date(now.getFullYear(), now.getMonth() - monthsAgo, 1)
          const from = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`
          const to   = new Date(d.getFullYear(), d.getMonth()+1, 0).toISOString().slice(0,10)
          const lbl  = monthsAgo === 0 ? 'This month' : d.toLocaleString('en-GB', { month: 'short', year: 'numeric' })
          const active = dateFrom === from && dateTo === to
          return (
            <button key={monthsAgo} onClick={() => { setDateFrom(from); setDateTo(to) }}
              style={{ padding: '6px 12px', borderRadius: 6, border: `1px solid ${active ? '#111317' : '#e9e9e4'}`, background: active ? '#111317' : '#fff', color: active ? '#fff' : '#6b7280', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
              {lbl}
            </button>
          )
        })}

        <div style={{ flex: 1 }} />

        {/* Search */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#f6f6f3', border: '1px solid #e9e9e4', borderRadius: 7, padding: '7px 11px' }}>
          <span style={{ fontSize: 13, color: '#9a9a94' }}>🔍</span>
          <input value={searchInput} onChange={e => setSearchInput(e.target.value)} placeholder="Shop name, owner, phone…"
            style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 12, width: 170, fontFamily: "'Plus Jakarta Sans',sans-serif" }} />
          {searchInput && <span onClick={() => { setSearchInput(''); setSearch('') }} style={{ cursor: 'pointer', color: '#9a9a94', fontSize: 14 }}>×</span>}
        </div>

        {/* Category filter */}
        <select value={category} onChange={e => setCategory(e.target.value)}
          style={{ border: '1px solid #e9e9e4', borderRadius: 7, padding: '8px 11px', fontSize: 12, fontWeight: 600, color: '#111317', background: '#fff', cursor: 'pointer', outline: 'none', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
          <option value="">All Categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>

        {/* Verification filter */}
        <select value={verification} onChange={e => setVerification(e.target.value)}
          style={{ border: `1px solid ${verification ? (verification === 'verified' ? '#bbf7d0' : verification === 'blocked' || verification === 'rejected' ? '#fecaca' : '#bfdbfe') : '#e9e9e4'}`, borderRadius: 7, padding: '8px 11px', fontSize: 12, fontWeight: 600, color: verification ? (verification === 'verified' ? '#15803d' : verification === 'blocked' || verification === 'rejected' ? '#b91c1c' : '#1d4ed8') : '#111317', background: '#fff', cursor: 'pointer', outline: 'none', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
          <option value="">All Verification</option>
          <option value="verified">✓ Verified</option>
          <option value="pending">⏳ Pending Review</option>
          <option value="unverified">⚠ Unverified (no docs)</option>
          <option value="rejected">✕ Rejected</option>
          <option value="blocked">⛔ Blocked (Unverified + Rejected)</option>
        </select>
      </div>

      {/* Period label */}
      <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginBottom: (search || category || verification) ? 10 : 16 }}>
        Report period: <b style={{ color: '#111317' }}>{periodLabel}</b> · {totalCount.toLocaleString()} shops · {sumOrders.toLocaleString()} orders
        {meta.ipay_pct > 0 && <> · iPay {(meta.ipay_pct * 100).toFixed(0)}% · Platform {(meta.platform_pct * 100).toFixed(0)}% base{commPct > 0 ? ` · Delivery comm ${commPct}%` : ''} · <span style={{ color: '#b45309' }}>tier discounts applied per-row</span></>}
      </div>

      {/* Search / filter active banner */}
      {(search || category || verification) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, background: verification === 'blocked' || verification === 'rejected' ? '#fef2f2' : '#eff6ff', border: `1px solid ${verification === 'blocked' || verification === 'rejected' ? '#fecaca' : '#bfdbfe'}`, borderRadius: 8, padding: '9px 14px' }}>
          <span style={{ fontSize: 13 }}>{verification === 'blocked' || verification === 'rejected' ? '⛔' : verification === 'verified' ? '✓' : '🔍'}</span>
          <div style={{ flex: 1 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: verification === 'blocked' || verification === 'rejected' ? '#b91c1c' : '#1d4ed8' }}>Filtered results</span>
            {search && <span style={{ fontSize: 12, color: '#374151' }}> · "{search}"</span>}
            {category && <span style={{ fontSize: 12, color: '#374151' }}> · {category}</span>}
            {verification && <span style={{ fontSize: 12, fontWeight: 700, color: verification === 'blocked' || verification === 'rejected' ? '#b91c1c' : verification === 'verified' ? '#15803d' : '#1d4ed8' }}>
              {' · '}{{'verified':'✓ Verified','pending':'⏳ Pending','unverified':'⚠ Unverified','rejected':'✕ Rejected','blocked':'⛔ Payout Blocked'}[verification]}
            </span>}
            <span style={{ fontSize: 11, color: '#6b7280', marginLeft: 6 }}>— CSV and PDF will reflect this filter</span>
          </div>
          <button
            onClick={() => { setSearchInput(''); setSearch(''); setCategory(''); setVerification('') }}
            style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px' }}>
            Clear all ×
          </button>
        </div>
      )}

      {/* Summary cards — totals cover ALL matching shops (all pages) */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <SummaryCard
          label="Total Product Payout"
          value={money(sumProduct)}
          accent="#15803d" bold
          sub={`gross sales minus iPay & platform fees · ${totalCount} shops`}
        />
        {sumDelivGross > 0 && (
          <SummaryCard
            label="Self-Delivery Net"
            value={money(sumDelivNet)}
            accent="#1d4ed8"
            sub={commPct > 0 ? `after ${commPct}% platform cut · gross ${money(sumDelivGross)}` : 'delivery fee seller handled'}
          />
        )}
        {commPct > 0 && sumDelivComm > 0 && (
          <SummaryCard
            label={`Delivery Commission (${commPct}%)`}
            value={money(sumDelivComm)}
            accent="#e8440a"
            sub="platform cut from seller-handled delivery"
          />
        )}
        {sumTips > 0 && (
          <SummaryCard
            label="Tips Net Payout"
            value={money(sumTips)}
            accent="#7c3aed"
            sub={meta.ipay_pct > 0
              ? `${sumTipsCount.toLocaleString()} tips · gross ${money(sumTipsGross)} − iPay ${money(sumTipsIpay)}`
              : `${sumTipsCount.toLocaleString()} tips across all matched shops`}
          />
        )}
        <SummaryCard
          label="Grand Total Payout"
          value={money(sumTotal)}
          accent="#111317" bold
          sub={`product + delivery + tips · ${sumOrders.toLocaleString()} orders`}
        />
      </div>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 16px', marginBottom: 14, fontSize: 13, color: '#b91c1c' }}>⚠ {error}</div>
      )}

      {/* Main table */}
      <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, overflow: 'hidden', marginBottom: 16 }}>
        {/* Table header */}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1.1fr 0.7fr 1.1fr 1fr 0.9fr 1.2fr', padding: '10px 18px', borderBottom: '1px solid #e9e9e4', background: '#f9f9f7' }}>
          {['Shop', 'Category', 'Owner', 'Orders', 'Product Payout', commPct > 0 ? 'Delivery (Gross→Net)' : 'Self-Delivery', 'Tips', 'Total Payout'].map(h => (
            <div key={h} style={{ fontSize: 9, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: "'JetBrains Mono',monospace" }}>{h}</div>
          ))}
        </div>

        {loading ? (
          <div style={{ padding: '48px 0', textAlign: 'center', color: '#9a9a94', fontSize: 13 }}>Loading seller payout report…</div>
        ) : shops.length === 0 ? (
          <div style={{ padding: '48px 0', textAlign: 'center', color: '#9a9a94', fontSize: 13 }}>No shops found for this period.</div>
        ) : (
          <>
            {shops.map((s, i) => {
              const bg = avatarBg(s.shop_name || '')
              return (
                <div key={s.shop_id || i}
                  onClick={() => setSelected(s)}
                  style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1.1fr 0.7fr 1.1fr 1fr 0.9fr 1.2fr', padding: '12px 18px', borderBottom: i < shops.length - 1 ? '1px solid #f3f3f0' : 'none', cursor: 'pointer', alignItems: 'center', transition: 'background 0.1s' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#fafaf8'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  {/* Shop name */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                    <div style={{ width: 34, height: 34, borderRadius: 8, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, flexShrink: 0 }}>{initials(s.shop_name)}</div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.shop_name || '—'}</div>
                      <div style={{ fontSize: 10, color: '#9a9a94' }}>{s.owner_phone || '—'}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 4, flexWrap: 'wrap' }}>
                        <TierBadge tier={s.tier} small />
                        {s.payout_blocked && (
                          <span style={{ fontSize: 8, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase', color: '#b91c1c', background: '#fef2f2', border: '1px solid #fecaca', padding: '1px 5px', borderRadius: 3, whiteSpace: 'nowrap' }}>
                            ⛔ {s.verification_status === 'rejected' ? 'Rejected' : 'Unverified'}
                          </span>
                        )}
                      </div>
                      {/* Final payout breakdown */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 11, fontWeight: 900, color: '#111317', fontFamily: "'JetBrains Mono',monospace" }}>{moneyN(s.total_payout)}</span>
                        <span style={{ fontSize: 9, color: '#d1d5db' }}>RWF</span>
                        <span style={{ fontSize: 9, color: '#d1d5db', margin: '0 1px' }}>·</span>
                        <span style={{ fontSize: 9, color: '#15803d', fontFamily: "'JetBrains Mono',monospace" }}>P {moneyN(s.product_payout)}</span>
                        {Number(s.self_delivery_payout) > 0 && <>
                          <span style={{ fontSize: 9, color: '#d1d5db' }}>+</span>
                          <span style={{ fontSize: 9, color: '#1d4ed8', fontFamily: "'JetBrains Mono',monospace" }}>D {moneyN(s.self_delivery_payout)}</span>
                        </>}
                        {Number(s.tips_total) > 0 && <>
                          <span style={{ fontSize: 9, color: '#d1d5db' }}>+</span>
                          <span style={{ fontSize: 9, color: '#7c3aed', fontFamily: "'JetBrains Mono',monospace" }}>T {moneyN(s.tips_total)}</span>
                        </>}
                      </div>
                    </div>
                  </div>

                  {/* Category */}
                  <div>
                    <span style={{ display: 'inline-block', background: '#f3f4f6', color: '#374151', fontSize: 9, fontWeight: 700, padding: '3px 7px', borderRadius: 4, textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: "'JetBrains Mono',monospace" }}>
                      {s.category || '—'}
                    </span>
                  </div>

                  {/* Owner */}
                  <div style={{ fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.owner_name || '—'}</div>

                  {/* Orders */}
                  <div style={{ fontSize: 13, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: '#374151' }}>{s.order_count}</div>

                  {/* Product payout */}
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace", color: '#15803d' }}>{moneyN(s.product_payout)}</div>
                    <div style={{ fontSize: 9, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginTop: 1 }}>of {moneyN(s.gross_subtotal)} gross</div>
                    {s.tier_discount > 0 && (
                      <div style={{ fontSize: 8, color: '#15803d', fontFamily: "'JetBrains Mono',monospace", marginTop: 1 }}>
                        {(s.platform_pct_effective * 100).toFixed(0)}% fee · saved {moneyN(Number(s.gross_subtotal || 0) * s.tier_discount)} RWF
                      </div>
                    )}
                  </div>

                  {/* Self-delivery */}
                  <div>
                    {Number(s.self_delivery_gross) > 0 ? (
                      <>
                        <div style={{ fontSize: 13, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace", color: '#1d4ed8' }}>{moneyN(s.self_delivery_payout)}</div>
                        {commPct > 0 && (
                          <div style={{ fontSize: 9, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginTop: 1 }}>
                            {moneyN(s.self_delivery_gross)} −{commPct}%
                          </div>
                        )}
                      </>
                    ) : (
                      <div style={{ fontSize: 10, color: '#d1d5db', fontStyle: 'italic' }}>—</div>
                    )}
                  </div>

                  {/* Tips — net payout after iPay fee + platform tip commission */}
                  <div>
                    {Number(s.tips_total) > 0 ? (
                      <>
                        <div style={{ fontSize: 13, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace", color: '#7c3aed' }}>{moneyN(s.tips_total)}</div>
                        <div style={{ fontSize: 9, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginTop: 1 }}>
                          {s.tips_count} tip{Number(s.tips_count) !== 1 ? 's' : ''}
                        </div>
                        {(Number(s.tips_ipay_total) > 0 || Number(s.tips_platform_total) > 0) && (
                          <div style={{ fontSize: 8, color: '#b91c1c', fontFamily: "'JetBrains Mono',monospace", marginTop: 1 }}>
                            gross {moneyN(s.tips_gross_total)} − iPay {moneyN(s.tips_ipay_total)} − plat {moneyN(s.tips_platform_total)}
                          </div>
                        )}
                      </>
                    ) : (
                      <div style={{ fontSize: 10, color: '#d1d5db', fontStyle: 'italic' }}>—</div>
                    )}
                  </div>

                  {/* Total payout */}
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 900, fontFamily: "'JetBrains Mono',monospace", color: '#111317' }}>{moneyN(s.total_payout)}</div>
                  </div>
                </div>
              )
            })}

            {/* Page footer totals */}
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1.1fr 0.7fr 1.1fr 1fr 0.9fr 1.2fr', padding: '12px 18px', borderTop: '2px solid #e9e9e4', background: '#f9f9f7', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 800, color: '#111317' }}>Page {page} of {totalPages}</div>
                <div style={{ fontSize: 9, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginTop: 1 }}>{shops.length} shops shown</div>
              </div>
              <div /><div />
              <div style={{ fontSize: 12, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace" }}>
                {shops.reduce((s, r) => s + Number(r.order_count || 0), 0)}
              </div>
              <div style={{ fontSize: 13, fontWeight: 900, fontFamily: "'JetBrains Mono',monospace", color: '#15803d' }}>{moneyN(pageProduct)}</div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 900, fontFamily: "'JetBrains Mono',monospace", color: '#1d4ed8' }}>{moneyN(pageDelivNet)}</div>
                {commPct > 0 && pageDelivGross > 0 && (
                  <div style={{ fontSize: 9, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginTop: 1 }}>
                    gross {moneyN(pageDelivGross)}
                  </div>
                )}
              </div>
              <div>
                {pageTips > 0 ? (
                  <>
                    <div style={{ fontSize: 13, fontWeight: 900, fontFamily: "'JetBrains Mono',monospace", color: '#7c3aed' }}>{moneyN(pageTips)}</div>
                    {shops.reduce((s,r)=>s+Number(r.tips_ipay_total||0)+Number(r.tips_platform_total||0),0) > 0 && (
                      <div style={{ fontSize: 8, color: '#b91c1c', fontFamily: "'JetBrains Mono',monospace", marginTop: 1 }}>
                        gross {moneyN(shops.reduce((s,r)=>s+Number(r.tips_gross_total||0),0))}
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ fontSize: 10, color: '#d1d5db' }}>—</div>
                )}
              </div>
              <div style={{ fontSize: 14, fontWeight: 900, fontFamily: "'JetBrains Mono',monospace", color: '#111317' }}>{moneyN(pageGrandTotal)}</div>
            </div>
          </>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>
            Page {page} of {totalPages} · {totalCount.toLocaleString()} shops
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <PagBtn label="← Prev" disabled={page === 1}          onClick={() => setPage(p => p - 1)} />
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const p = Math.max(1, Math.min(page - 2, totalPages - 4)) + i
              return <PagBtn key={p} label={p} active={p === page} onClick={() => setPage(p)} />
            })}
            <PagBtn label="Next →" disabled={page === totalPages} onClick={() => setPage(p => p + 1)} />
          </div>
        </div>
      )}

      {/* Seller drawer */}
      {selected && (
        <SellerDrawer
          shop={selected}
          dateFrom={dateFrom}
          dateTo={dateTo}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  )
}
